import React from "react";
import styled from "styled-components";
import {
    CalendarConnection,
    CalendarProvider,
} from "@lifeat/shared/data-models/user/integrations/IntegrationsDataEntity";
import { LifeAtButtonV2 } from "@common/LifeAtButtonV2/LifeAtButtonV2";
import { useUserContext } from "@contexts/UserContext";
import GoogleLogo from "@resources/logos/integrations/Google/google_logo.png";
import OutlookLogo from "@resources/logos/integrations/Outlook/outlook_logo.png";
import Styles from "@themes/Styles";
import { clearCacheReloadPage } from "@util/BrowserUtil";
import CalendarSignInButtonRow from "./CalendarSignInButtonRow";

const Root = styled.div`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 16px;
`;

const Section = styled.section`
    display: flex;
    flex-direction: column;
    align-items: stretch;
    gap: 8px;
`;

const SectionHeader = styled.h2`
    font-size: 16px;
`;

const ConnectionList = styled.div`
    display: grid;
    grid-template-columns: auto 1fr auto [end];
    border: 1px solid ${({ theme }) => theme.borderPrimary};
    border-radius: ${Styles.borderRadiusSharp};
    font-size: 14px;
    min-width: fit-content;
    background: ${({ theme }) => theme.backgroundSecondary};
`;

const ConnectionListRow = styled.div`
    display: grid;
    grid-template-columns: subgrid;
    grid-column: 1 / end;

    &:not(:first-child) {
        border-top: 1px solid ${({ theme }) => theme.borderPrimary};
    }

    align-items: center;
    padding: 8px;
`;

const IconImg = styled.img`
    width: 20px;
    height: auto;
`;

const ConnectionEmail = styled.span`
    margin: 0 8px;
`;

type Props = {
    onCalendarConnectionRemove: (connection: CalendarConnection) => void;
};

export function CalendarManagement(props: Props) {
    const { userData } = useUserContext();

    const connections = userData?.integrationsData?.calendar ?? [];

    return (
        <Root>
            <Section>
                <SectionHeader>Add a new calendar</SectionHeader>
                <CalendarSignInButtonRow />
            </Section>
            <Section>
                <SectionHeader>Calendar accounts</SectionHeader>
                <ConnectionList>
                    {connections.map((connection) => (
                        <ConnectionListRow key={`${connection.provider}-${connection.email}`}>
                            <IconImg
                                src={(() => {
                                    switch (connection.provider) {
                                        case CalendarProvider.GOOGLE:
                                            return GoogleLogo;
                                        case CalendarProvider.OUTLOOK:
                                            return OutlookLogo;
                                    }
                                })()}
                            />
                            <ConnectionEmail>{connection.email}</ConnectionEmail>
                            <LifeAtButtonV2
                                onClick={() => {
                                    props.onCalendarConnectionRemove(connection);
                                }}
                            >
                                Disconnect
                            </LifeAtButtonV2>
                        </ConnectionListRow>
                    ))}
                </ConnectionList>
                <Section style={{ width: "100%", display: "flex", justifyContent: "flex-end", marginTop: "16px" }}>
                    <LifeAtButtonV2
                        onClick={() => {
                            const result = confirm(
                                "By resetting, your browser will refresh and you may need to reconnect your calendars.",
                            );
                            if (result) {
                                clearCacheReloadPage();
                            }
                        }}
                    >
                        <div
                            style={{
                                color: "red",
                                fontSize: "0.8rem",
                            }}
                        >
                            Reset Calendars
                        </div>
                    </LifeAtButtonV2>
                </Section>
            </Section>
        </Root>
    );
}
